import { useEffect, useRef, useState } from "react";
import { StreamingHelper } from "@scrile/streaming-client/build";
import {
  NetworkQualityLevel,
  QualityLimitationReason,
  RawNetworkSample,
  qualityFromSample,
  reduceRtcStats,
  sampleDelta,
} from "lib/networkQualityHelpers";

export type { NetworkQualityLevel, QualityLimitationReason } from "lib/networkQualityHelpers";

export interface NetworkStats {
  rtt: number;
  packetLoss: number;
  jitter: number;
  bitrate: number;
  jitterBufferDelay: number;
  qualityLimitationReason: QualityLimitationReason;
  freezeCount: number;
  framesDropped: number;
}

const POLL_INTERVAL_MS = 2000;
// First sample after a transport rebuild (initial connect, ICE-restart
// reconnect) has RTT=0/jitter=0/loss=0 because there's no prior sample
// to delta against. Showing the badge as "5 bars" for one tick then
// crashing to whatever the real value is reads as a flicker. We
// suppress the first reading and start emitting from the second tick.
const SUPPRESS_FIRST_TICKS = 1;

export default function useNetworkQuality(webRTC: StreamingHelper, enabled = true) {
  const [quality, setQuality] = useState<NetworkQualityLevel>(5);
  const [stats, setStats] = useState<NetworkStats | null>(null);
  const prevSampleRef = useRef<RawNetworkSample | null>(null);

  useEffect(() => {
    if (!enabled) return;

    // Reset baseline so a re-mount (transport rebuilt during reconnect)
    // doesn't keep deltaing against pre-rebuild counters.
    prevSampleRef.current = null;
    let ticksSeen = 0;

    const interval = setInterval(async () => {
      const client = webRTC.webRTCData?.producer?.client || webRTC.webRTCData?.consumer?.client;
      if (!client) return;

      let rtcStats: any[];
      try {
        rtcStats = await client.getStats();
      } catch {
        return;
      }

      const sample = reduceRtcStats(rtcStats);
      const delta = sampleDelta(sample, prevSampleRef.current);
      const isFirstSample = prevSampleRef.current === null;
      prevSampleRef.current = sample;
      ticksSeen += 1;

      // Wait until we have a real delta to work with, otherwise the
      // first frame after a reconnect shows misleadingly perfect stats.
      if (isFirstSample || ticksSeen <= SUPPRESS_FIRST_TICKS) return;

      const next: NetworkStats = {
        rtt: sample.rtt,
        bitrate: sample.bitrate,
        jitter: sample.jitter,
        jitterBufferDelay: sample.jitterBufferDelay,
        qualityLimitationReason: sample.qualityLimitationReason,
        freezeCount: sample.freezeCount,
        framesDropped: sample.framesDropped,
        packetLoss: delta.packetLoss,
      };
      setStats(next);
      setQuality(qualityFromSample(next, delta));
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [webRTC, enabled]);

  return { quality, stats };
}
